import React, { useState } from 'react'
import { useAtom } from 'jotai'
import { useQuery } from '@tanstack/react-query'
import { SwitchTabBar } from '../../common/SwitchTabBar'
import { ChatConversations } from '../Conversations'
import { useAdminOrBoardMemberInAtLeastOneSociety } from './useAdminOrBoardMemberInAtLeastOneSociety'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { TabBar } from '../../common/TabBar'
import { ChatHeaderButtons } from '../ChatHeaderButtons'
import { useTabBarOptions } from './useTabBarOptions'
import { ChatHeaderState, chatHeaderStateAtom } from '../atom'
import { ChatFilter } from '../ChatFilter/ChatFilter'
import { notificationsQueries } from '../../../api/notifications'
import { useAuth } from '../../../hooks/useAuth'
import { ActiveTabBar, getConversationType } from '../../../views/chat'
import GetToKnow from '../../society/GetToKnow/GetToKnow'
import { i18nSocietyType } from '../../../i18n'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'

interface ChatSideBarContentProps {
  activeTabBar: ActiveTabBar
  setActiveTab: React.Dispatch<React.SetStateAction<number>>
  setActiveTabBar: (index: number) => void
  activeTab: number
}

export const ChatSideBarContent = ({
  setActiveTabBar,
  setActiveTab,
  activeTabBar,
  activeTab,
}: ChatSideBarContentProps): JSX.Element => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const [, setChatHeaderState] = useAtom(chatHeaderStateAtom)

  const ActiveTabTitle = {
    0: translate('chatRoomsSidebar.activeTabBar.resident'),
    1: translate('chatRoomsSidebar.activeTabBar.board', {
      context: i18nSocietyType(society?.societyTypeDisplayName),
    }),
  }

  const isAdminOrBoardMemberInAtLeastOneSociety =
    useAdminOrBoardMemberInAtLeastOneSociety()
  const { getTabs } = useTabBarOptions(activeTabBar)

  const resetStartChatAtoms = (): void => {
    setChatHeaderState(ChatHeaderState.None)
    setActiveTab(0)
  }

  const { userId } = useAuth()
  const { data: notification } = useQuery(
    notificationsQueries.getUserUnseenUnreadCount(userId)
  )

  const unseenResidentChatCount =
    (notification?.unseen.oneOnOneCount || 0) +
    (notification?.unseen.interestsCount || 0)
  const unseenBoardCount = notification?.unseen.boardCount || 0

  const getUnseenCountString = (type: 'resident' | 'board'): string => {
    const _count =
      type === 'resident' ? unseenResidentChatCount : unseenBoardCount

    if (_count > 0) {
      return ` (${_count})`
    }

    return ''
  }

  const [societyFilter, setSocietyFilter] = useState<string[]>([])

  return (
    <div className="flex h-full flex-col space-y-6 pt-8">
      <GetToKnow
        className="mx-4"
        title={translate('getToKnowScreen.screens.chat.title', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        description={translate('getToKnowScreen.screens.chat.description', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        getToKnowId="chat"
      />
      {isAdminOrBoardMemberInAtLeastOneSociety && (
        <>
          <SwitchTabBar
            tabs={[
              {
                title: `${ActiveTabTitle[ActiveTabBar.RESIDENT]}${getUnseenCountString('resident')}`,
              },
              {
                title: `${ActiveTabTitle[ActiveTabBar.BOARD]}${getUnseenCountString('board')}`,
              },
            ]}
            activeTabBar={activeTabBar}
            setActiveTabBar={setActiveTabBar}
            customOnClick={resetStartChatAtoms}
          />
        </>
      )}
      <TabBar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={getTabs()}
        disableScroll
        className="relative"
      >
        <ChatFilter setSocietyFilter={setSocietyFilter} />
      </TabBar>
      <ChatHeaderButtons activeTabBar={activeTabBar} activeTab={activeTab} />
      <ChatConversations
        type={getConversationType(activeTabBar, activeTab)}
        societyIds={societyFilter}
      />
    </div>
  )
}
